import * as signalR from "@microsoft/signalr";
import getItemFromLocalStorage from "../../utils/getItemFromLocalStorage";

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnecting = false;
    this.retryCount = 0;
    this.maxRetries = 10;
    this.eventHandlers = new Map();
    this.reconnectTimer = null;
    // Guard flag to prevent onclose from triggering attemptReconnect()
    // when stop() is called intentionally (e.g. unmount or logout)
    this.isStopping = false;
  }

  createConnection() {
    const token = getItemFromLocalStorage("accessToken");
    if (!token) {
      return null;
    }

    const notifyUrl = `${process.env.REACT_APP_BACK_END_SERVER_BASE_URL}notify`;
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(notifyUrl, {
        accessTokenFactory: () => token || "",
        transport: signalR.HttpTransportType.WebSockets, // Force WebSockets only
        skipNegotiation: true,
        withCredentials: false
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 5s, 10s, 30s, then 60s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 5000;
          if (retryContext.previousRetryCount === 3) return 10000;
          if (retryContext.previousRetryCount === 4) return 30000;
          return 60000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupConnectionHandlers();
    return this.connection;
  }

  setupConnectionHandlers() {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      this.isConnecting = true;
    });

    this.connection.onreconnected((connectionId) => {
      this.isConnecting = false;
      this.retryCount = 0;
      this.resubscribeEventHandlers();
    });

    /* 
      onclose fires both when withAutomaticReconnect() exhausts all retries
      AND when stop() is called intentionally. Without the isStopping guard,
      calling stop() would trigger attemptReconnect(), creating a zombie
      reconnection loop. The custom reconnect is still needed for:
       1. Initial start() failure (withAutomaticReconnect only works after a successful connection)
       2. Fallback after built-in reconnect exhausts its retry policy
    */
    this.connection.onclose((error) => {
      this.isConnecting = false;
      if (!this.isStopping) {
        this.attemptReconnect();
      }
    });
  }

  logTransportType() {
    if (!this.connection) return;

      console.log(`SignalR Transport Type: WebSockets`);
      console.log(`Connection Details:`, {
        state: this.connection.state,
        connectionId: this.connection.connectionId,
        transport: this.connection.connection.transport?.constructor?.name
      });
    
  }

  async start() {
    const token = getItemFromLocalStorage("accessToken");
    if (!token) {
      return false;
    }

    if (this.isConnecting) {
      return false;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return true;
    }

    if (!this.connection) {
      this.createConnection();
      this.resubscribeEventHandlers();
    }

    this.isConnecting = true;

    try {
      await this.connection.start();
      this.isConnecting = false;
      this.retryCount = 0;
      return true;
    } catch (error) {
      this.isConnecting = false;
      this.attemptReconnect();
      return false;
    }
  }

  attemptReconnect() {
    if (this.retryCount >= this.maxRetries) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = this.getReconnectDelay(this.retryCount);

    this.reconnectTimer = setTimeout(async () => {
      this.retryCount++;
      const token = getItemFromLocalStorage("accessToken");
      
      if (!token) {
        return;
      }

      await this.start();
    }, delay);
  }

  getReconnectDelay(retryCount) {
    // Exponential backoff: 0s, 2s, 5s, 10s, 30s, then 60s
    if (retryCount === 0) return 0;
    if (retryCount === 1) return 2000;
    if (retryCount === 2) return 5000;
    if (retryCount === 3) return 10000;
    if (retryCount === 4) return 30000;
    return 60000;
  }

  async stop() {
    // Set before connection.stop() so the onclose handler knows
    // this is an intentional shutdown, not an unexpected disconnect
    this.isStopping = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {}
    }

    this.isConnecting = false;
    // Reset so the service can be restarted cleanly if needed
    this.isStopping = false;
    this.retryCount = 0;
    this.eventHandlers.clear();
  }

  on(eventName, handler) {
    // Always buffer the handler for resubscription after reconnect,
    // even if this.connection is null. This allows callers to register
    // handlers before start() — they'll be applied to the connection
    // once it's created via resubscribeEventHandlers() in start().
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set());
    }
    this.eventHandlers.get(eventName).add(handler);

    // If connection already exists, register immediately
    if (this.connection) {
      this.connection.on(eventName, handler);
    }
  }

  off(eventName, handler) {
    if (!this.connection) return;

    if (this.eventHandlers.has(eventName)) {
      this.eventHandlers.get(eventName).delete(handler);
      if (this.eventHandlers.get(eventName).size === 0) {
        this.eventHandlers.delete(eventName);
      }
    }

    this.connection.off(eventName, handler);
  }

  resubscribeEventHandlers() {
    this.eventHandlers.forEach((handlers, eventName) => {
      handlers.forEach(handler => {
        this.connection.on(eventName, handler);
      });
    });
  }

  getConnectionState() {
    if (!this.connection) return "Disconnected";
    
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connected:
        return "Connected";
      case signalR.HubConnectionState.Connecting:
        return "Connecting";
      case signalR.HubConnectionState.Reconnecting:
        return "Reconnecting";
      case signalR.HubConnectionState.Disconnected:
        return "Disconnected";
      case signalR.HubConnectionState.Disconnecting:
        return "Disconnecting";
      default:
        return "Unknown";
    }
  }

  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

const signalRService = new SignalRService();
export default signalRService;
